import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import Layout from '../../components/Layout';
import { useTranslation } from 'react-i18next';
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AlertModal from '../../components/AlertModal';
import AddButton from '../../components/Button/AddButton';
import { useNavigate, useLocation } from 'react-router-dom';
import type { InfoMessage } from '../../types';

const InfoMessageListScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    infoMessageStore: { items, fetchAll, remove },
    churchStore: { churches, fetchChurches },
    userStore: { state: authState },
  } = useMst();

  const [search, setSearch] = useState('');
  const [churchId, setChurchId] = useState<string | 'all'>('all');
  const location = useLocation();
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Fetch on mount and ensure churches are available
  useEffect(() => {
    fetchAll();
    if (churches.length === 0) fetchChurches('', true);
  }, [fetchAll, fetchChurches, churches.length]);

  // Fallback: if list remains empty after login, force a reload
  useEffect(() => {
    if (authState === 'LOGGED_IN' && items.length === 0) {
      fetchAll();
    }
  }, [authState, items.length, fetchAll]);

  // Reload when tab becomes visible again (e.g., after returning from create page)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && authState === 'LOGGED_IN') {
        fetchAll();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [authState, fetchAll]);

  // Refetch when route gains focus (navigate back to list)
  useEffect(() => {
    fetchAll();
  }, [location.key, fetchAll]);

  const typedItems = Array.from(items) as unknown as InfoMessage[];
  const filtered: InfoMessage[] = typedItems.filter((m) => {
    const s = search.trim().toLowerCase();
    const matchesSearch = !s || (m.title ?? '').toLowerCase().includes(s);
    const matchesChurch = churchId === 'all' || (churchId === 'null' ? !m.churchId : m.churchId === churchId);
    return matchesSearch && matchesChurch;
  });

  const refresh = () => {
    const cid = churchId === 'all' ? undefined : churchId === 'null' ? null : churchId;
    fetchAll(search || undefined, cid ?? undefined);
  };

  const askDelete = (id?: string) => {
    if (!id) return;
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteId(undefined);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    await remove(deleteId);
    setConfirmOpen(false);
    setDeleteId(undefined);
    refresh();
  };

  return (
    <Layout title={t('infoMessages.list') ?? 'Info-viestit'}>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={6}>
          <TextField
            size="small"
            fullWidth
            label={t('search') ?? 'Search'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') refresh();
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="church-filter">{t('church.title') ?? 'Church'}</InputLabel>
            <Select
              labelId="church-filter"
              label={t('church.title') ?? 'Church'}
              value={churchId}
              onChange={(e) => setChurchId(e.target.value as string)}
            >
              <MenuItem value={'all'}>{t('all') ?? 'All'}</MenuItem>
              <MenuItem value={'null'}>Ei seurakuntaa</MenuItem>
              {churches.map((c) => (
                <MenuItem key={c.id} value={c.id ?? ''}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3} display="flex" gap={1}>
          <Button variant="outlined" onClick={refresh}>
            {t('filter') ?? 'Filter'}
          </Button>
          <AddButton onClick={() => navigate('/info-messages/create')} text={t('infoMessages.create') ?? 'Create'} />
        </Grid>
      </Grid>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('infoMessages.title') ?? 'Title'}</TableCell>
            <TableCell>{t('church.title') ?? 'Church'}</TableCell>
            <TableCell>{t('active') ?? 'Active'}</TableCell>
            <TableCell>{t('status') ?? 'Status'}</TableCell>
            <TableCell>{t('updated') ?? 'Updated'}</TableCell>
            <TableCell>{t('actions') ?? 'Actions'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((m) => (
            <TableRow key={m.id} hover>
              <TableCell>{m.title}</TableCell>
              <TableCell>{m.churchId ? churches.find((c) => c.id === m.churchId)?.name : 'Ei seurakuntaa'}</TableCell>
              <TableCell>{m.isActive ? t('active') : t('inactive')}</TableCell>
              <TableCell>{m.status ?? 'draft'}</TableCell>
              <TableCell>{m.updatedAt ? new Date(m.updatedAt).toLocaleString() : ''}</TableCell>
              <TableCell>
                <Button size="small" variant="text" onClick={() => navigate(`/info-messages/${m.id}`)}>
                  {t('edit') ?? 'Edit'}
                </Button>
                <IconButton size="small" color="error" onClick={() => askDelete(m.id)} aria-label="delete">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <AlertModal
        open={confirmOpen}
        onClose={cancelDelete}
        title={'delete'}
        leftButtonPress={cancelDelete}
        rightButtonPress={confirmDelete}
      />
    </Layout>
  );
});

export default InfoMessageListScreen;
